import { useEffect, useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ChartResponse,
  getGetApiPlacementAssuranceVieQueryKey,
  useGetApiPlacementAssuranceVie,
  useGetApiPlacementDetails,
} from '@/api';
import ArbitrageProposalBanner from '@/components/ArbitrageProposalBanner/ArbitrageProposalBanner';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import CardGererContrat from '@/components/CardGererContrat/CardGererContrat';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ContractDetailsHeader from '@/components/ContractDetailsHeader/ContractDetailsHeader';
import { ContractDocumentsTab } from '@/components/ContractDocumentsTab/ContractDocumentsTab';
import DemoTag from '@/components/DemoTag/DemoTag';
import FixedActionsBar from '@/components/FixedActionsBar/FixedActionsBar';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import Tabs from '@/components/Tabs/Tabs';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { HIDDEN_PERFORMANCE_GESTIONNAIRE_LABELS } from '@/constants/gestionnaire';
import { ROUTE } from '@/constants/route';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import { useArbitrages } from '@/hooks/useArbitrages';
import { useAuth } from '@/hooks/useAuth';
import useCancelModal from '@/hooks/useCancelModal';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalCalculPerformance from '@/modals/ContentModalCalculPerformance/ContentModalCalculPerformance';
import { PopinAssuranceVie } from '@/modals/ContentModalCalculPerformance/data';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { OperationContrat } from '@/pages/OperationContrat';
import { Repartition } from '@/pages/Repartition/Repartition';
import { SupportsDetails } from '@/pages/SupportsDetails';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { DetailsRepartitionResponse } from '@/services/types/commonResponse';
import { useUserStore } from '@/store/useStore';
import { performanceFilter } from '@/utils/functions/performanceFilter/performanceFilter';

import styles from './styles.module.css';

export const AssuranceVieDetails: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { windowWidth } = useScreenSize();
  const { isDemo } = useDemoContext();
  const userConseiller = useUserStore((state) => state.user);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const [showModalVersementProg, setShowModalVersementProg] = useState<boolean>(false);
  const [showModalCalculPerf, setShowModalCalculPerf] = useState<boolean>(false);
  const { showCancelModal, openCancelModal, closeCancelModal } = useCancelModal();

  const { data, isLoading, isError } = useGetApiPlacementAssuranceVie(id ?? '');
  const {
    data: dataRepartition,
    isLoading: isLoadingRepartition,
    isError: isErrorRepartition,
  } = useGetApiPlacementDetails(data?.placementId as string);

  const { data: arbitrageProposals, isLoading: isArbitrageProposalsLoading } = useArbitrages(
    { idPlacement: data?.placementId ?? undefined },
    { query: { enabled: !!data?.placementId } }
  );

  const [performance, setPerformance] = useState<number | null>(0);
  const [plusMoinsValue, setPlusMoinsValue] = useState<number | null>(0);
  const isPerformanceHidden = HIDDEN_PERFORMANCE_GESTIONNAIRE_LABELS.includes(data?.gestionnaire ?? '');
  const valorisationEncours = data?.valorisation?.dateEncours
    ? data.valorisation.dateEncours.split('-').reverse().join('/')
    : '--/--/----';

  const { filterPerformanceInvestment } = performanceFilter(data as ChartResponse);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Mes contrats', path: ROUTE.CONTRATS },
    { label: data?.nomProduit, path: ROUTE.ASSURANCEVIE },
  ];

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showModalCalculPerf') {
      setShowModalCalculPerf(true);
    } else if (modal === 'showModalVersementProg') {
      setShowModalVersementProg(true);
    }
  };

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    setShowModalCalculPerf(false);
    setShowModalVersementProg(false);
  };

  useEffect(() => {
    if (data) {
      filterPerformanceInvestment({ lineChartTimespan, setPerformance, setPlusMoinsValue });
    }
  }, [data, lineChartTimespan]);

  useEffect(() => {
    if (data) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: `mes contrats - ${data?.nomProduit}`,
        page_template: 'espace client',
        user_id: user?.id,
        user_client: user?.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: `mes contrats - ${data?.nomProduit} - supports`,
        event_page: `mes contrats - ${data?.nomProduit} - supports`,
      });
    }
  }, [data, user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isError || isErrorRepartition) {
    navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });
    return null;
  }

  if (!data) {
    return <Loader />;
  }

  const subscriptionDate = data?.dateOuverture ? data.dateOuverture.split('-').reverse().join('/') : null;

  return (
    <>
      {(isLoading || isLoadingRepartition || !data) && <Loader />}
      <div className={styles.contractDetailsContainer}>
        {showModalCalculPerf && (
          <ModaleDefaut
            content={ContentModalCalculPerformance({ ...PopinAssuranceVie.args })}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            btnFullwidth={false}
            onClickBtn={() => closeModal()}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {showModalVersementProg && (
          <ModaleDefaut
            content={<p className="center-text">{t('modals.scheduledPaymentInfoModal.descriptionText')}</p>}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            btnFullwidth={false}
            onClickBtn={() => closeModal()}
            onClickClose={() => closeModal()}
            mobileStyle="stick-to-bottom"
            size="large"
          />
        )}

        {showCancelModal && (
          <ContentModalRenonciation
            nomProduit={data?.nomProduit ?? ''}
            placementId={data?.placementId ?? ''}
            delayDays={data?.cancelablePeriod ?? 30}
            onClose={closeCancelModal}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: getGetApiPlacementAssuranceVieQueryKey(id ?? '') });
              closeCancelModal();
            }}
          />
        )}

        <Breadcrumb items={breadcrumbItems} />

        <div>
          <div className={styles.contractDetailsHeader}>
            <ContractDetailsHeader
              nomProduit={data?.nomProduit}
              isCancelable={data?.isCancelable}
              cancelablePeriod={data?.cancelablePeriod ?? 30}
              manager={data?.gestionnaire}
              openCancelModal={openCancelModal}
            />
            {FEATURE_FLAGS.ARBITRAGE &&
              arbitrageProposals &&
              !isArbitrageProposalsLoading &&
              windowWidth > BREAKPOINTS.mobile && <ArbitrageProposalBanner proposals={arbitrageProposals} />}

            <div className={styles.contractDetailsValuationAmountDate}>
              <div className={styles.lifeInsuranceValuation}>
                <span className={styles.valuation}>
                  {t('format.currencyFlexible', { value: data?.valorisation?.encours ?? 0 })}
                </span>
                <button
                  className={styles.contractDetailsValuationInfo}
                  onClick={() => {
                    openModal('showModalCalculPerf');
                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                      event_id: 'clic',
                      event_categorie: 'clic',
                      event_metier: 'espace client placement',
                      event_nom: 'info assurance vie',
                      event_detail_1: 'picto',
                    });
                  }}
                >
                  <i className="totem-icon totem-icon-info" />
                </button>
                {isDemo && <DemoTag />}
              </div>

              <p className={styles.contractDetailsValuationDate}>
                {t('lifeInsurance.description')} {valorisationEncours}
              </p>
            </div>

            <TrendingValue
              value={isPerformanceHidden ? null : plusMoinsValue}
              secondaryValue={isPerformanceHidden ? null : performance}
              icon={!isPerformanceHidden && plusMoinsValue !== null}
            />
          </div>

          <div>
            <LineChart timespan={lineChartTimespan} donnees={data.chartData ?? []} />
          </div>

          <div className={styles.filterButtonContent}>
            {dataButtonFiltre.map((value, index, buttonsFiltre) => (
              <ChipsRadioSmall
                key={value.value}
                label={windowWidth <= BREAKPOINTS.mobile ? value.textMobile : value.text}
                name="lineChartButtons"
                value={value.value}
                defaultCheck={index + 1 === buttonsFiltre.length}
                onClick={() => {
                  setLineChartTimespan(value.value);
                  filterPerformanceInvestment({ lineChartTimespan: value.value, setPerformance, setPlusMoinsValue });
                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.tracking);
                }}
              />
            ))}
          </div>
        </div>

        {FEATURE_FLAGS.ARBITRAGE && arbitrageProposals && windowWidth <= BREAKPOINTS.mobile && (
          <ArbitrageProposalBanner proposals={arbitrageProposals} />
        )}

        <Tabs
          tabs={[
            {
              label: 'Supports',
              disabled: false,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'supports',
                  event_detail_1: 'onglet',
                });
              },
              content: <SupportsDetails dataSupports={dataRepartition as DetailsRepartitionResponse} />,
            },
            {
              label: 'Contrat',
              disabled: false,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'contrat',
                  event_detail_1: 'onglet',
                });
              },
              content: (
                <div className={styles.contractDetailsBody}>
                  <div className={styles.contractDetailsSection}>
                    <div className={styles.contractDetailsSectionHeader}>
                      <h2 className={styles.contractDetailsSectionTitle}>
                        {t('lifeInsurance.title')}
                        {isDemo && <DemoTag />}
                      </h2>
                      <div
                        style={{
                          width: 114,
                          height: 32,
                          backgroundImage: `url("${data.gestionnaireLogo}")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                        }}
                      ></div>
                    </div>

                    <MtxGrid row>
                      <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                        <table className={styles.contractDetailsTable}>
                          <tbody className={styles.contractDetailsList}>
                            {data?.numContrat ? (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.contractNumberLabel')}</p>
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>{data?.numContrat}</p>
                                </td>
                              </tr>
                            ) : null}
                            {subscriptionDate ? (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.openingDateLabel')}</p>{' '}
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>{subscriptionDate}</p>
                                </td>
                              </tr>
                            ) : null}
                            {data?.horizonPlacement ? (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.investmentHorizonLabel')}</p>
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>{data?.horizonPlacement}</p>
                                </td>
                              </tr>
                            ) : null}

                            {data?.modeGestion && (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.managementModeLabel')}</p>{' '}
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>{data.modeGestion}</p>
                                </td>
                              </tr>
                            )}
                            {typeof data?.versementInitial === 'number' && (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.initialDepositLabel')}</p>
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>{t('format.currencyFlexible', { value: data?.versementInitial })}</p>
                                </td>
                              </tr>
                            )}

                            <tr className={styles.contractDetailsLine}>
                              <td className={styles.contractDetailsTitle}>
                                <p>{t('lifeInsurance.scheduledDepositLabel')}</p>
                                <button
                                  className={styles.contractDetailsMoreInfo}
                                  onClick={() => {
                                    openModal('showModalVersementProg');
                                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                                      event_id: 'clic',
                                      event_categorie: 'clic',
                                      event_metier: 'espace client placement',
                                      event_nom: 'info versements programmes',
                                      event_detail_1: 'picto',
                                    });
                                  }}
                                >
                                  <i className="totem-icon totem-icon-info" />
                                </button>
                              </td>
                              <td className={styles.contractDetailsValue}>
                                <p>
                                  {typeof data.versementProgramme === 'number' && data?.versementProgramme > 0
                                    ? t('format.currencyFlexible', { value: data?.versementProgramme })
                                    : '--'}{' '}
                                </p>
                              </td>
                            </tr>
                            {data?.versementProgrammePeriodicite && (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.debitFrequencyLabel')}</p>
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p> {data?.versementProgrammePeriodicite}</p>
                                </td>
                              </tr>
                            )}

                            {typeof data?.montantTotalRachete === 'number' && (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.totalPaidLabel')}</p>{' '}
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>
                                    {typeof data.montantTotalVerse === 'number'
                                      ? t('format.currencyFlexible', { value: data?.montantTotalVerse })
                                      : 'xxx'}{' '}
                                  </p>
                                </td>
                              </tr>
                            )}

                            {typeof data?.montantTotalRachete === 'number' && (
                              <tr className={styles.contractDetailsLine}>
                                <td className={styles.contractDetailsTitle}>
                                  <p>{t('lifeInsurance.totalRedeemedLabel')}</p>
                                </td>
                                <td className={styles.contractDetailsValue}>
                                  <p>
                                    {data.montantTotalRachete == null
                                      ? 'xxx'
                                      : t('format.currencyFlexible', { value: data?.montantTotalRachete })}
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </MtxGrid>
                    </MtxGrid>
                  </div>

                  <div className={styles.contractDetailsSectionCard}>
                    <h2>{t('lifeInsurance.subtitle')}</h2>
                    <MtxGrid row>
                      <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4}>
                        <CardGererContrat
                          insuranceLink={data.gestionnaireAC ?? ''}
                          insuranceLogo={data.gestionnaireLogo}
                          contractName={data.nomProduit}
                          isLogo={true}
                          isAsideInfo={false}
                        />
                      </MtxGrid>
                    </MtxGrid>
                  </div>
                </div>
              ),
            },
            {
              label: 'Répartition',
              disabled: false,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'repartition',
                  event_detail_1: 'onglet',
                });
              },
              content: <Repartition placementId={data?.placementId ?? ''} data={data} />,
            },
            {
              label: 'Mouvements',
              disabled: data?.dernieresOperations?.length === 0,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'mouvements',
                  event_detail_1: 'onglet',
                });
              },
              content: <OperationContrat data={data?.dernieresOperations} placementId={data?.placementId} />,
            },
            {
              label: 'Documents',
              disabled: false,
              onClick: () => {
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'documents',
                  event_detail_1: 'onglet',
                });
              },
              content: (
                <div className={styles.contractDetailsBody}>
                  <ContractDocumentsTab
                    documents={data?.documents}
                    paymentSlipsSubtitle={t('lifeInsurance.paymentSlipsSubtitle')}
                    documentsTabTitle={t('common.documentsTab')}
                    otherDocumentsTabTitle={t('common.otherDocumentsTab')}
                    partnerCardDescription={t('common.documentsCardDescription')}
                    insuranceLink={data.gestionnaireAC ?? ''}
                    insuranceLogo={data.gestionnaireLogo}
                    contractName={data.nomProduit}
                  />
                </div>
              ),
            },
          ]}
        />

        <FixedActionsBar centered>
          <MtxButton
            className={styles.fixedActionsBarButton}
            onClick={async () => {
              await void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'faire un versement',
                event_detail_1: 'cta',
              });
              navigate(ROUTE.SELECTOPERATION, {
                state: {
                  key: 'versement',
                  infoContrat: data,
                },
              });
            }}
            variant="primary"
            size="large"
            fullWidth
            disabled={!!userConseiller?.advisorView}
          >
            <span>{t('depositCard.buttonText')}</span>
          </MtxButton>
        </FixedActionsBar>
      </div>
    </>
  );
};
